import { AccountValueChartTimeHorizonType, GrowthChecklistModuleData, NewsfeedItem } from 'types/account';
import {
	ChecklistItemType,
	GrowthCalculatorByAgeModuleData,
	InvitationProgramModuleData,
	ModularDashboardChangeAccountTypeModule,
	ModularDashboardInvestmentStreaksModule,
	ModularDashboardPortfolioOverview,
	ModularDashboardProgressModule,
	ModularDashboardResumeCheckoutModule
} from 'types/modular-dashboard/custom';
import { ModularDashboardData, ModularDashboardMetadata } from 'types/modular-dashboard';
import { FeaturedCard } from 'types/layout';
import { investmentEntityRequest } from '@utils/request';
import { ModularDashboardDeltaItem } from 'types/modular-dashboard/delta-module';
import { PerformanceContextCardData } from 'types/performance-context';
import { useAppStore } from '@stores/app';

export const dismissDashboardModule = async (module: string): Promise<void> => {
	await investmentEntityRequest.post(`/dashboard-modules/dismiss`, { module });
};

export const getModularDashboardData = async (): Promise<ModularDashboardData> => {
	const response = await investmentEntityRequest.get(`/modular-dashboard`);
	return response.data;
};

export const getModularDashboardMetadata = async (): Promise<ModularDashboardMetadata> => {
	const response = await investmentEntityRequest.get(`/modular-dashboard/metadata`);
	return response.data;
};

export const getDeltaModuleData = async (moduleName: string): Promise<ModularDashboardDeltaItem> => {
	const response = await investmentEntityRequest.get(`/modules/DELTA/${moduleName}`);
	return response.data;
};

export const getFeaturedModuleData = async (moduleName: string): Promise<FeaturedCard> => {
	const response = await investmentEntityRequest.get(`/modules/FEATURED_CONTENT/${moduleName}`, {
		suppressToast: () => true
	});
	return response.data;
};

export const getGrowthChecklistModuleData = async (): Promise<GrowthChecklistModuleData> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/GROWTH_CHECKLIST', {
		suppressToast: () => true
	});
	return response.data;
};

export const getNewsfeedModuleData = async (): Promise<Array<NewsfeedItem>> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/NEWSFEED', {
		suppressToast: () => true
	});
	return response.data;
};

export const getPortfolioOverviewModuleData = async (): Promise<ModularDashboardPortfolioOverview> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/PORTFOLIO_OVERVIEW', {
		suppressToast: () => true
	});
	return response.data;
};

export const getProgressModuleData = async (): Promise<ModularDashboardProgressModule> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/PROGRESS', {
		suppressToast: () => true
	});
	return response.data;
};

export const loadNextProgressModule = async (): Promise<ModularDashboardProgressModule> => {
	const response = await investmentEntityRequest.post('/modules/CUSTOM/PROGRESS/next-milestone', {
		suppressToast: () => true
	});
	return response.data;
};

export const getPerformanceContextData = async (
	timeRange: AccountValueChartTimeHorizonType
): Promise<Array<PerformanceContextCardData>> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/ACCOUNT_VALUE_CHART', {
		params: {
			timeRange
		},
		suppressToast: () => true
	});
	return response.data;
};

export const getInvitationProgramModule = async (): Promise<InvitationProgramModuleData> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/INVITATION_PROGRAM', {
		suppressToast: () => true
	});
	return response.data;
};

export const getGrowthCalculatorByAgeModule = async (): Promise<GrowthCalculatorByAgeModuleData> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/GROWTH_CALCULATOR_BY_AGE', {
		suppressToast: () => true
	});
	return response.data;
};

export const getChangeAccountTypeModuleData = async (): Promise<ModularDashboardChangeAccountTypeModule> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/CHANGE_ACCOUNT_TYPE', {
		suppressToast: () => true
	});
	return response.data;
};

export const trackChecklistItem = async (eventName: ChecklistItemType): Promise<void> => {
	const appStore = useAppStore();
	if (!appStore.isReadOnlyAccess) {
		await investmentEntityRequest.post('/tracked-event', { type: eventName });
	}
};

export const getResumeCheckoutModule = async (): Promise<ModularDashboardResumeCheckoutModule> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/RESUME_CHECKOUT', {
		suppressToast: () => true
	});
	return response.data;
};

export const getInvestmentStreakModuleData = async (): Promise<ModularDashboardInvestmentStreaksModule> => {
	const response = await investmentEntityRequest.get('/modules/CUSTOM/INVESTMENT_STREAK', {
		suppressToast: () => true
	});
	return response.data;
};
